import React from 'react';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import type { StatusTone } from '@/domain/warehouse';
import { LibraryViewToggle } from '../../ui/LibraryViewToggle';
import '../events/EventsCalendarView.css';
import './WindowCalendarView.css';

/**
 * Windows as bars across days (Month, Week and Day modes): transfers, subrentals, anything with a
 * first and a last day. Data-agnostic: callers map their rows to items and, where they need to,
 * render the marker, tooltip and Day-view details themselves. Extracted from the Transfers
 * calendar (`TransferOrdersCalendarView` is now an adapter over this and renders exactly as before).
 *
 * Colours: an item with a `tone` is drawn in the shared status-chip colours (theme tokens, see
 * WindowCalendarView.css); an item without one takes the next colour of the bar palette.
 */

type CalendarMode = 'month' | 'week' | 'day';

type CalendarDay = string | dayjs.Dayjs;

export type WindowCalendarItem<TData = unknown> = {
  id: string;
  /** Bold short code on the bar ("PXR0002002"). */
  code: string;
  title: string;
  /** Shown after the title on Week bars ("Cincinnati to Columbus"), and in the default tooltip. */
  subtitle?: string | null;
  /** First day of the window: `YYYY-MM-DD` or a dayjs. */
  start: CalendarDay;
  /** Last day of the window (inclusive); the first day when missing. */
  end?: CalendarDay | null;
  /** A later day drawn as a whisker after the bar, e.g. a temporary transfer's expected return. */
  returnAt?: CalendarDay | null;
  /** The record page the bar opens. */
  href: string;
  /** Status-chip colours instead of the bar palette. */
  tone?: StatusTone | null;
  /** The status pill in Day view and the default tooltip. */
  statusLabel?: string | null;
  /** The caller's row, handed back to the render props. */
  data: TData;
};

type WindowEntry<TData> = Omit<WindowCalendarItem<TData>, 'start' | 'end' | 'returnAt'> & {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  returnAt: dayjs.Dayjs | null;
};

type LaneEntry<TData> = WindowEntry<TData> & {
  lane: number;
  colorIndex: number;
};

type WeekBar<TData> = {
  entry: LaneEntry<TData>;
  startCol: number;
  endCol: number;
  continues: boolean;
  started: boolean;
  showBar: boolean;
  returnCol: number | null;
};

type TooltipState<TData> = {
  entry: WindowEntry<TData>;
  x: number;
  y: number;
};

export type WindowCalendarViewProps<TData> = {
  items: ReadonlyArray<WindowCalendarItem<TData>>;
  /** Called with the visible range (`YYYY-MM-DD`) whenever the mode or the anchor day changes. */
  onDateRangeChange?: (start: string, end: string) => void;
  /** Extra class on the root, e.g. `cal-root--transfers`. */
  className?: string;
  /** Muted note after the title, e.g. "(12 transfers)". */
  countLabel?: React.ReactNode;
  /** Accessible name of the Month/Week/Day switch. */
  modeAriaLabel?: string;
  /** Shows a checkbox that hides and shows the `returnAt` whiskers (shown by default). */
  returnWhiskerToggle?: { label: string; title: string } | null;
  /** Day view with nothing on it. */
  emptyDayLabel?: string;
  /** Accessible name of a whisker link; the bar itself is named by its text. */
  openLabel?: (item: WindowCalendarItem<TData>) => string;
  /** A small mark before the code (bars and Day view), e.g. a status dot. */
  renderMarker?: (item: WindowCalendarItem<TData>) => React.ReactNode;
  /** The tooltip's content; defaults to code, status, title, dates and subtitle. */
  renderTooltip?: (item: WindowCalendarItem<TData>) => React.ReactNode;
  /** Extra class on the tooltip. */
  tooltipClassName?: string;
  /** The Day-view rows under each window's name; defaults to its dates and subtitle. */
  renderDayDetails?: (item: WindowCalendarItem<TData>, dateRange: string) => React.ReactNode;
  /** Drawn under the calendar, e.g. a status legend. */
  legend?: React.ReactNode;
};

const BAR_COLORS = [
  '#2563EB',
  '#DC2626',
  '#059669',
  '#D97706',
  '#7C3AED',
  '#DB2777',
  '#0891B2',
  '#CA8A04',
  '#4F46E5',
  '#E11D48',
  '#0D9488',
  '#EA580C',
  '#6D28D9',
  '#0284C7',
  '#65A30D',
  '#BE185D',
  '#B45309',
  '#047857',
  '#6366F1',
  '#9333EA',
  '#0E7490',
  '#C2410C',
  '#1D4ED8',
  '#A21CAF',
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MODE_OPTIONS = [
  { value: 'month' as const, label: 'Month' },
  { value: 'week' as const, label: 'Week' },
  { value: 'day' as const, label: 'Day' },
];

function toDay(value: CalendarDay | null | undefined): dayjs.Dayjs | null {
  if (!value) return null;
  const parsed = typeof value === 'string' ? dayjs(value) : value;
  return parsed.isValid() ? parsed.startOf('day') : null;
}

function formatDate(value: dayjs.Dayjs | null): string {
  return value?.isValid() ? value.format('MMM D, YYYY') : '--';
}

function formatDateRange(entry: WindowEntry<unknown>): string {
  return entry.start.isSame(entry.end, 'day')
    ? formatDate(entry.start)
    : `${formatDate(entry.start)} - ${formatDate(entry.end)}`;
}

function toEntries<TData>(items: ReadonlyArray<WindowCalendarItem<TData>>): WindowEntry<TData>[] {
  const out: WindowEntry<TData>[] = [];
  for (const item of items) {
    const startDay = toDay(item.start);
    if (!startDay) continue;
    const endDay = toDay(item.end) ?? startDay;
    out.push({
      ...item,
      start: endDay.isBefore(startDay) ? endDay : startDay,
      end: endDay.isBefore(startDay) ? startDay : endDay,
      returnAt: toDay(item.returnAt),
    });
  }
  return out;
}

/** The item as the caller passed it, for the render props. */
function itemOf<TData>(entry: WindowEntry<TData>): WindowCalendarItem<TData> {
  return entry;
}

function effectiveStart(entry: WindowEntry<unknown>): dayjs.Dayjs {
  return entry.start;
}

function effectiveEnd(entry: WindowEntry<unknown>, showReturnWhiskers: boolean): dayjs.Dayjs {
  if (showReturnWhiskers && entry.returnAt && entry.returnAt.isAfter(entry.end)) {
    return entry.returnAt;
  }
  return entry.end;
}

function assignLanesSimple<TData>(entries: WindowEntry<TData>[], showReturnWhiskers: boolean): LaneEntry<TData>[] {
  const sorted = [...entries].sort((a, b) => {
    const startDiff = effectiveStart(a).diff(effectiveStart(b));
    if (startDiff !== 0) return startDiff;
    return effectiveEnd(b, showReturnWhiskers).diff(effectiveStart(b)) - effectiveEnd(a, showReturnWhiskers).diff(effectiveStart(a));
  });
  const laneEnds: dayjs.Dayjs[] = [];
  const result: LaneEntry<TData>[] = [];
  for (let index = 0; index < sorted.length; index += 1) {
    const entry = sorted[index];
    const entryStart = effectiveStart(entry);
    const entryEnd = effectiveEnd(entry, showReturnWhiskers);
    let chosenLane = -1;
    for (let lane = 0; lane < laneEnds.length; lane += 1) {
      if (entryStart.isAfter(laneEnds[lane])) {
        chosenLane = lane;
        break;
      }
    }
    if (chosenLane >= 0) {
      laneEnds[chosenLane] = entryEnd;
    } else {
      chosenLane = laneEnds.length;
      laneEnds.push(entryEnd);
    }
    result.push({ ...entry, lane: chosenLane, colorIndex: index % BAR_COLORS.length });
  }
  return result;
}

function getMonthWeeks(year: number, month: number): dayjs.Dayjs[][] {
  const first = dayjs().year(year).month(month).startOf('month');
  const startOfCalendar = first.startOf('week');
  const endOfCalendar = first.endOf('month').endOf('week');
  const weeks: dayjs.Dayjs[][] = [];
  let cursor = startOfCalendar;
  while (cursor.isBefore(endOfCalendar) || cursor.isSame(endOfCalendar, 'day')) {
    const week: dayjs.Dayjs[] = [];
    for (let day = 0; day < 7; day += 1) {
      week.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    weeks.push(week);
  }
  return weeks;
}

function getWeekDays(date: dayjs.Dayjs): dayjs.Dayjs[] {
  const start = date.startOf('week');
  return Array.from({ length: 7 }, (_, index) => start.add(index, 'day'));
}

/** Inline colour for palette bars; tone bars are coloured by WindowCalendarView.css. */
function barColor(entry: LaneEntry<unknown>): string | undefined {
  return entry.tone ? undefined : BAR_COLORS[entry.colorIndex];
}

type RenderProps<TData> = {
  openLabel: (item: WindowCalendarItem<TData>) => string;
  renderMarker?: (item: WindowCalendarItem<TData>) => React.ReactNode;
  renderTooltip?: (item: WindowCalendarItem<TData>) => React.ReactNode;
  tooltipClassName?: string;
  renderDayDetails?: (item: WindowCalendarItem<TData>, dateRange: string) => React.ReactNode;
  emptyDayLabel: string;
};

function WindowTooltip<TData>({ tooltip, render }: { tooltip: TooltipState<TData>; render: RenderProps<TData> }) {
  const { entry, x, y } = tooltip;
  const style: React.CSSProperties = {
    position: 'fixed',
    left: x + 12,
    top: y + 12,
    zIndex: 9999,
  };
  return (
    <div className={render.tooltipClassName ? `cal-tooltip ${render.tooltipClassName}` : 'cal-tooltip'} style={style}>
      {render.renderTooltip ? render.renderTooltip(itemOf(entry)) : (
        <>
          <div className="cal-tooltip__header">
            <span className="cal-tooltip__code">{entry.code}</span>
            {entry.statusLabel ? <span className="cal-tooltip__status pill subtle">{entry.statusLabel}</span> : null}
          </div>
          <div className="cal-tooltip__name">{entry.title}</div>
          <div className="cal-tooltip__row">{formatDateRange(entry)}</div>
          {entry.subtitle ? <div className="cal-tooltip__row">{entry.subtitle}</div> : null}
        </>
      )}
    </div>
  );
}

type SubViewProps<TData> = {
  entries: WindowEntry<TData>[];
  anchor: dayjs.Dayjs;
  tooltip: TooltipState<TData> | null;
  showReturnWhiskers: boolean;
  selectedDay?: dayjs.Dayjs | null;
  onHover: (entry: WindowEntry<TData> | null, event?: React.MouseEvent) => void;
  onDayClick?: (day: dayjs.Dayjs) => void;
  onDayDblClick?: (day: dayjs.Dayjs) => void;
  render: RenderProps<TData>;
};

function MonthView<TData>({ entries, anchor, tooltip, showReturnWhiskers, selectedDay, onHover, onDayClick, onDayDblClick, render }: SubViewProps<TData>) {
  const weeks = React.useMemo(() => getMonthWeeks(anchor.year(), anchor.month()), [anchor]);
  const today = dayjs().startOf('day');
  const currentMonth = anchor.month();
  const rangeStart = weeks[0][0];
  const rangeEnd = weeks[weeks.length - 1][6];
  const visibleEntries = React.useMemo(
    () => entries.filter((entry) => {
      const entryStart = effectiveStart(entry);
      const entryEnd = effectiveEnd(entry, showReturnWhiskers);
      return (entryStart.isBefore(rangeEnd) || entryStart.isSame(rangeEnd, 'day'))
        && (entryEnd.isAfter(rangeStart) || entryEnd.isSame(rangeStart, 'day'));
    }),
    [entries, rangeEnd, rangeStart, showReturnWhiskers],
  );
  const laneEntries = React.useMemo(() => assignLanesSimple(visibleEntries, showReturnWhiskers), [showReturnWhiskers, visibleEntries]);
  const weekBars = React.useMemo(() => weeks.map((week) => {
    const weekStart = week[0];
    const weekEnd = week[6];
    const bars: WeekBar<TData>[] = [];
    for (const entry of laneEntries) {
      const effectiveEntryEnd = effectiveEnd(entry, showReturnWhiskers);
      if (effectiveEntryEnd.isBefore(weekStart) || entry.start.isAfter(weekEnd)) continue;
      const barOverlaps = !(entry.end.isBefore(weekStart) || entry.start.isAfter(weekEnd));
      const clampedStart = barOverlaps ? (entry.start.isBefore(weekStart) ? weekStart : entry.start) : weekStart;
      const clampedEnd = barOverlaps ? (entry.end.isAfter(weekEnd) ? weekEnd : entry.end) : weekEnd;
      let returnCol: number | null = null;
      if (showReturnWhiskers && entry.returnAt && entry.returnAt.isAfter(entry.end)) {
        const returnClamped = entry.returnAt.isAfter(weekEnd) ? weekEnd : entry.returnAt;
        if (!returnClamped.isBefore(weekStart)) returnCol = returnClamped.day();
      }
      if (!barOverlaps && returnCol == null) continue;
      bars.push({
        entry,
        startCol: clampedStart.day(),
        endCol: clampedEnd.day(),
        continues: barOverlaps && entry.end.isAfter(weekEnd),
        started: barOverlaps && entry.start.isBefore(weekStart),
        showBar: barOverlaps,
        returnCol,
      });
    }
    return bars;
  }), [laneEntries, showReturnWhiskers, weeks]);
  const maxLanesPerWeek = weekBars.map((bars) => (bars.length === 0 ? 0 : Math.max(...bars.map((bar) => bar.entry.lane)) + 1));

  return (
    <div className="cal-month">
      <div className="cal-month__header">
        {DAY_LABELS.map((day) => (
          <div key={day} className="cal-month__day-label">{day}</div>
        ))}
      </div>
      <div className="cal-month__body">
        {weeks.map((week, weekIndex) => {
          const minHeight = 60 + maxLanesPerWeek[weekIndex] * 24;
          return (
            <div className="cal-month__week" key={week[0].format('YYYY-MM-DD')} style={{ minHeight }}>
              {week.map((day) => {
                const isToday = day.isSame(today, 'day');
                const isOtherMonth = day.month() !== currentMonth;
                const isSelected = selectedDay ? day.isSame(selectedDay, 'day') : false;
                return (
                  <div
                    key={day.format('YYYY-MM-DD')}
                    className={`cal-month__cell${isToday ? ' cal-month__cell--today' : ''}${isOtherMonth ? ' cal-month__cell--other' : ''}${isSelected ? ' cal-month__cell--selected' : ''}`}
                    onClick={() => onDayClick?.(day)}
                    onDoubleClick={() => onDayDblClick?.(day)}
                  >
                    <span className={`cal-month__date${isToday ? ' cal-month__date--today' : ''}`}>{day.date()}</span>
                  </div>
                );
              })}
              {weekBars[weekIndex].map((bar) => {
                const top = 26 + bar.entry.lane * 24;
                const color = barColor(bar.entry);
                if (!bar.showBar) {
                  const returnCol = bar.returnCol ?? 6;
                  const left = 0;
                  const width = ((returnCol + 1) / 7) * 100;
                  return (
                    <Link
                      key={`${bar.entry.id}-m-${weekIndex}-return`}
                      to={bar.entry.href}
                      className="cal-whisk cal-whisk--right"
                      data-tone={bar.entry.tone || undefined}
                      style={{
                        top,
                        left: `calc(${left}% + 2px)`,
                        width: `calc(${width}% - 4px)`,
                        height: 20,
                        '--whisk-color': color,
                      } as React.CSSProperties}
                      onMouseEnter={(event) => onHover(bar.entry, event)}
                      onMouseMove={(event) => onHover(bar.entry, event)}
                      onMouseLeave={() => onHover(null)}
                      aria-label={render.openLabel(itemOf(bar.entry))}
                      title={render.openLabel(itemOf(bar.entry))}
                    />
                  );
                }
                const span = bar.endCol - bar.startCol + 1;
                const leftPct = (bar.startCol / 7) * 100;
                const widthPct = (span / 7) * 100;
                const hasReturnWhisker = bar.returnCol != null && bar.returnCol > bar.endCol;
                return (
                  <React.Fragment key={`${bar.entry.id}-m-${weekIndex}`}>
                    {hasReturnWhisker && (
                      <Link
                        to={bar.entry.href}
                        className="cal-whisk cal-whisk--right"
                        data-tone={bar.entry.tone || undefined}
                        style={{
                          top,
                          left: `calc(${((bar.endCol + 1) / 7) * 100}% + 2px)`,
                          width: `calc(${((bar.returnCol! - bar.endCol) / 7) * 100}% - 4px)`,
                          height: 20,
                          '--whisk-color': color,
                        } as React.CSSProperties}
                        onMouseEnter={(event) => onHover(bar.entry, event)}
                        onMouseMove={(event) => onHover(bar.entry, event)}
                        onMouseLeave={() => onHover(null)}
                        aria-label={render.openLabel(itemOf(bar.entry))}
                        title={render.openLabel(itemOf(bar.entry))}
                      />
                    )}
                    <Link
                      to={bar.entry.href}
                      className={`cal-bar${bar.continues ? ' cal-bar--continues' : ''}${bar.started ? ' cal-bar--started' : ''}`}
                      data-tone={bar.entry.tone || undefined}
                      style={{
                        top,
                        left: `calc(${leftPct}% + 2px)`,
                        width: `calc(${widthPct}% - 4px)`,
                        backgroundColor: color,
                      }}
                      onMouseEnter={(event) => onHover(bar.entry, event)}
                      onMouseMove={(event) => onHover(bar.entry, event)}
                      onMouseLeave={() => onHover(null)}
                      title=""
                    >
                      <span className="cal-bar__label">
                        {render.renderMarker ? render.renderMarker(itemOf(bar.entry)) : null}
                        {!bar.started && <strong>{bar.entry.code}</strong>}
                        {!bar.started && ' '}
                        {bar.entry.title}
                      </span>
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
      </div>
      {tooltip && <WindowTooltip tooltip={tooltip} render={render} />}
    </div>
  );
}

function WeekView<TData>({ entries, anchor, tooltip, showReturnWhiskers, selectedDay, onHover, onDayClick, onDayDblClick, render }: SubViewProps<TData>) {
  const days = React.useMemo(() => getWeekDays(anchor), [anchor]);
  const today = dayjs().startOf('day');
  const rangeStart = days[0];
  const rangeEnd = days[6];
  const visibleEntries = React.useMemo(
    () => entries.filter((entry) => (
      (effectiveEnd(entry, showReturnWhiskers).isSame(rangeStart, 'day') || effectiveEnd(entry, showReturnWhiskers).isAfter(rangeStart))
      && (effectiveStart(entry).isSame(rangeEnd, 'day') || effectiveStart(entry).isBefore(rangeEnd))
    )),
    [entries, rangeEnd, rangeStart, showReturnWhiskers],
  );
  const laneEntries = React.useMemo(() => assignLanesSimple(visibleEntries, showReturnWhiskers), [showReturnWhiskers, visibleEntries]);
  const bars = React.useMemo((): WeekBar<TData>[] => {
    const result: WeekBar<TData>[] = [];
    for (const entry of laneEntries) {
      const barOverlaps = !(entry.end.isBefore(rangeStart) || entry.start.isAfter(rangeEnd));
      const clampedStart = barOverlaps ? (entry.start.isBefore(rangeStart) ? rangeStart : entry.start) : rangeStart;
      const clampedEnd = barOverlaps ? (entry.end.isAfter(rangeEnd) ? rangeEnd : entry.end) : rangeEnd;
      let returnCol: number | null = null;
      if (showReturnWhiskers && entry.returnAt && entry.returnAt.isAfter(entry.end)) {
        const returnClamped = entry.returnAt.isAfter(rangeEnd) ? rangeEnd : entry.returnAt;
        if (!returnClamped.isBefore(rangeStart)) returnCol = returnClamped.day();
      }
      if (!barOverlaps && returnCol == null) continue;
      result.push({
        entry,
        startCol: clampedStart.day(),
        endCol: clampedEnd.day(),
        continues: barOverlaps && entry.end.isAfter(rangeEnd),
        started: barOverlaps && entry.start.isBefore(rangeStart),
        showBar: barOverlaps,
        returnCol,
      });
    }
    return result;
  }, [laneEntries, rangeEnd, rangeStart, showReturnWhiskers]);
  const maxLane = bars.length > 0 ? Math.max(...bars.map((bar) => bar.entry.lane)) + 1 : 0;
  const bodyHeight = Math.max(200, 50 + maxLane * 32);

  return (
    <div className="cal-week">
      <div className="cal-week__header">
        {days.map((day) => {
          const isToday = day.isSame(today, 'day');
          const isSelected = selectedDay ? day.isSame(selectedDay, 'day') : false;
          return (
            <div
              key={day.format('YYYY-MM-DD')}
              className={`cal-week__day-label cal-week__day-label--click${isToday ? ' cal-week__day-label--today' : ''}${isSelected ? ' cal-week__day-label--selected' : ''}`}
              onClick={() => onDayClick?.(day)}
              onDoubleClick={() => onDayDblClick?.(day)}
              role="button"
              tabIndex={0}
            >
              <span className="cal-week__day-name">{DAY_LABELS[day.day()]}</span>
              <span className={`cal-week__day-num${isToday ? ' cal-week__day-num--today' : ''}`}>{day.date()}</span>
            </div>
          );
        })}
      </div>
      <div className="cal-week__body" style={{ minHeight: bodyHeight }}>
        {days.map((day) => {
          const isToday = day.isSame(today, 'day');
          const isSelected = selectedDay ? day.isSame(selectedDay, 'day') : false;
          return (
            <div
              key={day.format('YYYY-MM-DD')}
              className={`cal-week__col${isToday ? ' cal-week__col--today' : ''}${isSelected ? ' cal-week__col--selected' : ''}`}
            />
          );
        })}
        {bars.map((bar) => {
          const top = 8 + bar.entry.lane * 32;
          const color = barColor(bar.entry);
          if (!bar.showBar) {
            const returnCol = bar.returnCol ?? 6;
            const widthPct = ((returnCol + 1) / 7) * 100;
            return (
              <Link
                key={`${bar.entry.id}-week-return`}
                to={bar.entry.href}
                className="cal-whisk cal-whisk--right"
                data-tone={bar.entry.tone || undefined}
                style={{
                  top,
                  left: 'calc(0% + 3px)',
                  width: `calc(${widthPct}% - 6px)`,
                  height: 26,
                  '--whisk-color': color,
                } as React.CSSProperties}
                onMouseEnter={(event) => onHover(bar.entry, event)}
                onMouseMove={(event) => onHover(bar.entry, event)}
                onMouseLeave={() => onHover(null)}
                aria-label={render.openLabel(itemOf(bar.entry))}
                title={render.openLabel(itemOf(bar.entry))}
              />
            );
          }
          const span = bar.endCol - bar.startCol + 1;
          const leftPct = (bar.startCol / 7) * 100;
          const widthPct = (span / 7) * 100;
          const hasReturnWhisker = bar.returnCol != null && bar.returnCol > bar.endCol;
          return (
            <React.Fragment key={`${bar.entry.id}-week`}>
              {hasReturnWhisker && (
                <Link
                  to={bar.entry.href}
                  className="cal-whisk cal-whisk--right"
                  data-tone={bar.entry.tone || undefined}
                  style={{
                    top,
                    left: `calc(${((bar.endCol + 1) / 7) * 100}% + 3px)`,
                    width: `calc(${((bar.returnCol! - bar.endCol) / 7) * 100}% - 6px)`,
                    height: 26,
                    '--whisk-color': color,
                  } as React.CSSProperties}
                  onMouseEnter={(event) => onHover(bar.entry, event)}
                  onMouseMove={(event) => onHover(bar.entry, event)}
                  onMouseLeave={() => onHover(null)}
                  aria-label={render.openLabel(itemOf(bar.entry))}
                  title={render.openLabel(itemOf(bar.entry))}
                />
              )}
              <Link
                to={bar.entry.href}
                className={`cal-bar cal-bar--week${bar.continues ? ' cal-bar--continues' : ''}${bar.started ? ' cal-bar--started' : ''}`}
                data-tone={bar.entry.tone || undefined}
                style={{
                  top,
                  left: `calc(${leftPct}% + 3px)`,
                  width: `calc(${widthPct}% - 6px)`,
                  backgroundColor: color,
                }}
                onMouseEnter={(event) => onHover(bar.entry, event)}
                onMouseMove={(event) => onHover(bar.entry, event)}
                onMouseLeave={() => onHover(null)}
                title=""
              >
                <span className="cal-bar__label">
                  {render.renderMarker ? render.renderMarker(itemOf(bar.entry)) : null}
                  <strong>{bar.entry.code}</strong> {bar.entry.title}
                  {bar.entry.subtitle ? (
                    <span className="cal-bar__venue"> - {bar.entry.subtitle}</span>
                  ) : null}
                </span>
              </Link>
            </React.Fragment>
          );
        })}
      </div>
      {tooltip && <WindowTooltip tooltip={tooltip} render={render} />}
    </div>
  );
}

function DayView<TData>({ entries, anchor, tooltip, showReturnWhiskers, onHover, render }: SubViewProps<TData>) {
  const today = dayjs().startOf('day');
  const isToday = anchor.isSame(today, 'day');
  const dayEntries = React.useMemo(
    () => entries
      .filter((entry) => (
        (effectiveStart(entry).isSame(anchor, 'day') || effectiveStart(entry).isBefore(anchor))
        && (effectiveEnd(entry, showReturnWhiskers).isSame(anchor, 'day') || effectiveEnd(entry, showReturnWhiskers).isAfter(anchor))
      ))
      .sort((a, b) => effectiveStart(a).diff(effectiveStart(b)) || effectiveEnd(b, showReturnWhiskers).diff(effectiveStart(b)) - effectiveEnd(a, showReturnWhiskers).diff(effectiveStart(a))),
    [anchor, entries, showReturnWhiskers],
  );
  const laneEntries = React.useMemo(() => assignLanesSimple(dayEntries, showReturnWhiskers), [dayEntries, showReturnWhiskers]);

  return (
    <div className="cal-day">
      <div className={`cal-day__header${isToday ? ' cal-day__header--today' : ''}`}>
        <span className="cal-day__weekday">{DAY_LABELS[anchor.day()]}</span>
        <span className={`cal-day__num${isToday ? ' cal-day__num--today' : ''}`}>{anchor.date()}</span>
        <span className="cal-day__month">{anchor.format('MMMM YYYY')}</span>
      </div>
      <div className="cal-day__body">
        {laneEntries.length === 0 ? <div className="cal-day__empty">{render.emptyDayLabel}</div> : null}
        {laneEntries.map((entry) => {
          const dateRange = formatDateRange(entry);
          const isReturnOnly = showReturnWhiskers && Boolean(entry.returnAt) && anchor.isAfter(entry.end);
          const color = barColor(entry);
          return (
            <Link
              key={entry.id}
              to={entry.href}
              className={`cal-day__event${isReturnOnly ? ' cal-day__event--whisk-only' : ''}`}
              data-tone={entry.tone || undefined}
              style={{ borderLeftColor: color }}
              onMouseEnter={(event) => onHover(entry, event)}
              onMouseMove={(event) => onHover(entry, event)}
              onMouseLeave={() => onHover(null)}
            >
              <div className="cal-day__event-top">
                {render.renderMarker ? render.renderMarker(itemOf(entry)) : null}
                <strong className="cal-day__event-code">{entry.code}</strong>
                <span className="cal-day__event-name">{entry.title}</span>
                {entry.statusLabel != null ? <span className="pill subtle cal-day__event-status">{entry.statusLabel}</span> : null}
                {isReturnOnly ? <span className="pill subtle cal-day__event-whisk-label">Return</span> : null}
              </div>
              {render.renderDayDetails ? render.renderDayDetails(itemOf(entry), dateRange) : (
                <>
                  <div className="cal-day__event-meta text-xs fg-muted">{dateRange}</div>
                  {entry.subtitle ? <div className="cal-day__event-meta text-xs fg-muted">{entry.subtitle}</div> : null}
                </>
              )}
            </Link>
          );
        })}
      </div>
      {tooltip && <WindowTooltip tooltip={tooltip} render={render} />}
    </div>
  );
}

function defaultOpenLabel(item: WindowCalendarItem<unknown>): string {
  return `Open ${item.code}`;
}

export default function WindowCalendarView<TData>({
  items,
  onDateRangeChange,
  className,
  countLabel,
  modeAriaLabel = 'Calendar mode',
  returnWhiskerToggle,
  emptyDayLabel = 'Nothing on this day',
  openLabel = defaultOpenLabel,
  renderMarker,
  renderTooltip,
  tooltipClassName,
  renderDayDetails,
  legend,
}: WindowCalendarViewProps<TData>) {
  const [mode, setMode] = React.useState<CalendarMode>('month');
  const [anchor, setAnchor] = React.useState(() => dayjs().startOf('day'));
  const [selectedDay, setSelectedDay] = React.useState<dayjs.Dayjs | null>(null);
  const [tooltip, setTooltip] = React.useState<TooltipState<TData> | null>(null);
  const [showPicker, setShowPicker] = React.useState(false);
  const [pickerYear, setPickerYear] = React.useState(() => dayjs().year());
  const [showReturnWhiskers, setShowReturnWhiskers] = React.useState(true);
  const pickerRef = React.useRef<HTMLDivElement>(null);
  const entries = React.useMemo(() => toEntries(items), [items]);

  React.useEffect(() => {
    setPickerYear(anchor.year());
  }, [anchor]);

  React.useEffect(() => {
    if (!showPicker) return undefined;
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setShowPicker(false);
    }
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) setShowPicker(false);
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  React.useEffect(() => {
    if (!onDateRangeChange) return;
    let windowStart: dayjs.Dayjs;
    let windowEnd: dayjs.Dayjs;
    if (mode === 'month') {
      const weeks = getMonthWeeks(anchor.year(), anchor.month());
      windowStart = weeks[0][0];
      windowEnd = weeks[weeks.length - 1][6];
    } else if (mode === 'week') {
      windowStart = anchor.startOf('week');
      windowEnd = windowStart.add(6, 'day');
    } else {
      windowStart = anchor;
      windowEnd = anchor;
    }
    onDateRangeChange(windowStart.format('YYYY-MM-DD'), windowEnd.format('YYYY-MM-DD'));
  }, [anchor, mode, onDateRangeChange]);

  const handleHover = React.useCallback((entry: WindowEntry<TData> | null, event?: React.MouseEvent) => {
    if (!entry || !event) {
      setTooltip(null);
      return;
    }
    setTooltip({ entry, x: event.clientX, y: event.clientY });
  }, []);

  const handleDayClick = React.useCallback((day: dayjs.Dayjs) => {
    setSelectedDay((prev) => (prev && prev.isSame(day, 'day') ? null : day.startOf('day')));
  }, []);

  const handleDayDblClick = React.useCallback((day: dayjs.Dayjs) => {
    setSelectedDay(null);
    setAnchor(day.startOf('day'));
    setMode('day');
  }, []);

  function goToday() {
    setAnchor(dayjs().startOf('day'));
  }

  function goPrev() {
    if (mode === 'month') setAnchor((current) => current.subtract(1, 'month'));
    else if (mode === 'week') setAnchor((current) => current.subtract(1, 'week'));
    else setAnchor((current) => current.subtract(1, 'day'));
  }

  function goNext() {
    if (mode === 'month') setAnchor((current) => current.add(1, 'month'));
    else if (mode === 'week') setAnchor((current) => current.add(1, 'week'));
    else setAnchor((current) => current.add(1, 'day'));
  }

  function handleModeChange(nextMode: CalendarMode) {
    if (selectedDay) {
      setAnchor(selectedDay);
      setSelectedDay(null);
    }
    setMode(nextMode);
  }

  function handleWhiskerToggle(event: React.ChangeEvent<HTMLInputElement>) {
    setShowReturnWhiskers(event.currentTarget.checked);
  }

  const title = mode === 'month'
    ? anchor.format('MMMM YYYY')
    : mode === 'week'
      ? (() => {
        const weekStart = anchor.startOf('week');
        const weekEnd = weekStart.add(6, 'day');
        return weekStart.month() === weekEnd.month()
          ? `${weekStart.format('MMM D')} - ${weekEnd.format('D, YYYY')}`
          : `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
      })()
      : anchor.format('dddd, MMMM D, YYYY');

  const render: RenderProps<TData> = {
    openLabel,
    renderMarker,
    renderTooltip,
    tooltipClassName,
    renderDayDetails,
    emptyDayLabel,
  };

  const sharedProps: SubViewProps<TData> = {
    entries,
    anchor,
    tooltip,
    showReturnWhiskers,
    selectedDay,
    onHover: handleHover,
    onDayClick: handleDayClick,
    onDayDblClick: handleDayDblClick,
    render,
  };

  return (
    <div className={className ? `cal-root ${className}` : 'cal-root'}>
      <div className="cal-toolbar">
        <div className="cal-toolbar__nav">
          <button type="button" className="btn xs btn-outline cal-toolbar__arrow" onClick={goPrev} aria-label="Previous">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button type="button" className="btn xs btn-outline cal-toolbar__arrow" onClick={goNext} aria-label="Next">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
          <button type="button" className="btn xs btn-outline" onClick={goToday}>Today</button>
          <div className="cal-toolbar__title-wrap" ref={pickerRef}>
            <button
              type="button"
              className="cal-toolbar__title cal-toolbar__title--clickable"
              onClick={() => setShowPicker((current) => !current)}
              aria-expanded={showPicker}
              aria-haspopup="true"
            >
              {title}
              <svg className="cal-toolbar__title-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {showPicker ? (
              <div className="cal-picker dropdown-menu">
                <div className="cal-picker__year-row">
                  <button type="button" className="cal-picker__year-arrow" onClick={() => setPickerYear((year) => year - 1)} aria-label="Previous year">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                  </button>
                  <span className="cal-picker__year-label">{pickerYear}</span>
                  <button type="button" className="cal-picker__year-arrow" onClick={() => setPickerYear((year) => year + 1)} aria-label="Next year">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                </div>
                <div className="cal-picker__month-grid">
                  {MONTH_ABBR.map((label, index) => {
                    const isCurrent = anchor.year() === pickerYear && anchor.month() === index;
                    return (
                      <button
                        key={label}
                        type="button"
                        className={`cal-picker__month${isCurrent ? ' cal-picker__month--active' : ''}`}
                        onClick={() => {
                          setAnchor(dayjs().year(pickerYear).month(index).date(1).startOf('day'));
                          setShowPicker(false);
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
          {countLabel != null ? (
            <span className="text-xs fg-muted" style={{ marginLeft: '0.25rem' }}>
              {countLabel}
            </span>
          ) : null}
        </div>
        <div className="cal-toolbar__modes">
          {returnWhiskerToggle ? (
            <label className="cal-toolbar__toggle text-xs" title={returnWhiskerToggle.title}>
              <input
                type="checkbox"
                checked={showReturnWhiskers}
                onChange={handleWhiskerToggle}
              />
              <span>{returnWhiskerToggle.label}</span>
            </label>
          ) : null}
          <LibraryViewToggle
            value={mode}
            ariaLabel={modeAriaLabel}
            options={MODE_OPTIONS}
            onChange={handleModeChange}
          />
        </div>
      </div>

      {mode === 'month' ? <MonthView {...sharedProps} /> : null}
      {mode === 'week' ? <WeekView {...sharedProps} /> : null}
      {mode === 'day' ? <DayView {...sharedProps} /> : null}

      {legend}
    </div>
  );
}

export type WindowCalendarLegendItem = { tone: StatusTone; label: string };

/** The status legend under a tone calendar: one swatch per tone, in the order given. */
export function WindowCalendarLegend({ title, items }: { title: string; items: ReadonlyArray<WindowCalendarLegendItem> }) {
  return (
    <div className="cal-legend">
      <span className="cal-legend__title">{title}</span>
      <div className="cal-legend__items">
        {items.map((item) => (
          <span key={item.tone} className="cal-legend__item">
            <span className="window-calendar-legend__swatch" data-tone={item.tone} />
            <span className="cal-legend__label">{item.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
